import { useEffect, useRef, useState } from 'react';
import { Chip } from '@jotpsych/ui';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import LocalPharmacyOutlinedIcon from '@mui/icons-material/LocalPharmacyOutlined';
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import { formatIsoDate } from '../vm/formatters';

import {
  attributedFillsCopy,
  consentCtaLabel,
  consentNeededCopy,
  type ConsentState,
  emptyGrantedCopy,
  HISTORY_FILL_EXAMPLES,
  type HistoryFill,
  PULLING_COPY,
  RECONFIRM_CTA_LABEL,
  staleRefreshCopy,
  unavailableCopy,
} from './medicationHistoryConsent';

interface MedicationHistorySectionProps {
  /** Org subject language ("patient"/"client"); hosts pass useSubjectLanguageVariants().singular. */
  subject?: string;
  /**
   * DEMO mode (design-review stories): starting consent state; the CTA
   * advances needed → pulling → granted locally. Ignored when `state` is set.
   */
  initialState?: ConsentState;
  /** CONTROLLED mode (containers): the server-reported consent state. */
  state?: ConsentState;
  /**
   * `consent_stale` from the unified response: consent is on record but
   * DoseSpot rejected the last pull (GDN-3281). The section shows a
   * couldn't-refresh warning with a re-confirm affordance instead of passing
   * off whatever it has as a complete history.
   */
  consentStale?: boolean;
  /** The consent mutation is in flight — both consent CTAs disable to prevent double submits. */
  isRecordingConsent?: boolean;
  /** Fills shown once consent is granted. */
  fills?: HistoryFill[];
  /**
   * SureScripts fills that matched charted medications and render in those
   * medications' drawers instead of here. When `fills` is empty but this is
   * positive, the empty state explains where the fills went.
   */
  attributedFillCount?: number;
  /**
   * CONTROLLED mode: records consent via the host container (the real
   * mutation). When provided, the local demo simulation is disabled.
   */
  onRecordConsent?: () => void;
  /** Pharmacy history can run long — the section starts folded by default. */
  defaultExpanded?: boolean;
  /** Notification for the host's analytics — fired after the caret toggles. */
  onSectionToggled?: (expanded: boolean) => void;
  /**
   * Caps the expanded body with internal scroll. Dialog hosts pin the section
   * as a footer, so its content must not push the dialog around when opened.
   */
  contentMaxHeight?: number | string;
}

/** Milliseconds the "pulling" state shows before flipping to granted (demo). */
const PULL_SIMULATION_MS = 1200;

/** Chip summarizing the folded section so its state reads without expanding. */
const collapsedBadge = (
  state: ConsentState,
  fillCount: number,
  attributedFillCount: number,
  consentStale: boolean,
): { color: 'default' | 'warning' | 'info'; label: string } => {
  if (state === 'needed') return { color: 'warning', label: 'Consent needed' };
  if (state === 'pulling') return { color: 'info', label: 'Pulling…' };
  // The warning outranks a fill count: a folded "3 fills" chip would present
  // a blocked refresh as a healthy history (GDN-3281's false negative).
  if (consentStale) return { color: 'warning', label: 'Couldn’t refresh' };
  if (fillCount > 0)
    return { color: 'default', label: `${fillCount} fill${fillCount === 1 ? '' : 's'}` };
  if (attributedFillCount > 0)
    return { color: 'default', label: `${attributedFillCount} in medications above` };
  return { color: 'default', label: 'No fills' };
};

const SectionHeader: React.FC<{
  expanded: boolean;
  onToggle: () => void;
  badge: { color: 'default' | 'warning' | 'info'; label: string } | null;
}> = ({ expanded, onToggle, badge }) => (
  <Stack
    direction="row"
    spacing={1}
    onClick={onToggle}
    sx={{ alignItems: 'center', cursor: 'pointer' }}
  >
    <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
      Medication history
    </Typography>
    <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
      from pharmacies · SureScripts
    </Typography>
    {badge && (
      <Chip variant="soft" size="xSmall" color={badge.color} label={badge.label} />
    )}
    <IconButton
      size="small"
      aria-label={expanded ? 'Collapse medication history' : 'Expand medication history'}
      aria-expanded={expanded}
      onClick={(clickEvent) => {
        clickEvent.stopPropagation();
        onToggle();
      }}
    >
      {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
    </IconButton>
  </Stack>
);

const NeedsConsent: React.FC<{
  onConsent: () => void;
  subject: string;
  disabled?: boolean;
}> = ({ onConsent, subject, disabled }) => (
  <Box sx={{ py: 2, textAlign: 'center' }}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ mb: 1.5, maxWidth: 480, mx: 'auto' }}
    >
      {consentNeededCopy(subject)}
    </Typography>
    <Button
      variant="contained"
      size="small"
      onClick={onConsent}
      disabled={disabled}
      sx={{ textTransform: 'none' }}
    >
      {consentCtaLabel(subject)}
    </Button>
  </Box>
);

/**
 * Warning + re-confirm affordance for `consent_stale` (GDN-3281): consent is
 * on record but DoseSpot rejected the last pull, so the section must not pass
 * off its contents (empty or not) as a complete history. Re-confirming reuses
 * the same consent mutation as the first-time CTA — it clears the stale
 * marker and re-queues the pull.
 */
const StaleRefresh: React.FC<{
  onReconfirm: () => void;
  subject: string;
  disabled?: boolean;
}> = ({ onReconfirm, subject, disabled }) => (
  <Alert
    severity="warning"
    sx={{ my: 1, alignItems: 'center' }}
    action={
      <Button
        color="inherit"
        size="small"
        onClick={onReconfirm}
        disabled={disabled}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
      >
        {RECONFIRM_CTA_LABEL}
      </Button>
    }
  >
    {staleRefreshCopy(subject)}
  </Alert>
);

const Pulling: React.FC = () => (
  <Box sx={{ py: 1 }}>
    {[0, 1, 2, 3].map((i) => (
      <Skeleton key={i} variant="rounded" height={28} sx={{ my: 0.75, borderRadius: 1 }} />
    ))}
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', textAlign: 'center', mt: 1 }}
    >
      {PULLING_COPY}
    </Typography>
  </Box>
);

const FillRow: React.FC<{ fill: HistoryFill }> = ({ fill }) => {
  // A bare quantity ("900") reads as nothing without its day-supply pair.
  const dispensed =
    fill.quantity && fill.daysSupply
      ? `${fill.quantity} · ${fill.daysSupply}-day`
      : fill.quantity
        ? `${fill.quantity} dispensed`
        : fill.daysSupply
          ? `${fill.daysSupply}-day`
          : '';
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        alignItems: 'flex-start',
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <LocalPharmacyOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled', mt: 0.25 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {fill.drugName}
          </Typography>
          {fill.isControlled && (
            <Chip variant="soft" size="xSmall" color="warning" label="Controlled" />
          )}
        </Stack>
        <Typography
          variant="caption"
          color={fill.sig ? 'text.secondary' : 'text.disabled'}
          sx={{ display: 'block', fontStyle: fill.sig ? 'normal' : 'italic' }}
        >
          {fill.sig ?? 'Directions not on file'}
        </Typography>
        {fill.indication && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            For {fill.indication}
          </Typography>
        )}
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
          {fill.payer}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Filled {formatIsoDate(fill.lastFillDate)}
        </Typography>
        {dispensed && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
            {dispensed}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

const Granted: React.FC<{
  fills: HistoryFill[];
  subject: string;
  attributedFillCount: number;
}> = ({ fills, subject, attributedFillCount }) => {
  if (fills.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
        {attributedFillCount > 0
          ? attributedFillsCopy(subject, attributedFillCount)
          : emptyGrantedCopy(subject)}
      </Typography>
    );
  }
  return (
    <Box>
      {fills.map((fill) => (
        <FillRow key={fill.id} fill={fill} />
      ))}
    </Box>
  );
};

/**
 * Consent-gated pharmacy medication-history section for Proposal 0. Lives in the
 * same card as the current regimen (Edwin's "one place"), and renders one of the
 * four real consent states the unified endpoint reports. The CTA advances the
 * state locally so the gated → pulling → granted flow can be walked in Storybook.
 */
export const MedicationHistorySection: React.FC<MedicationHistorySectionProps> = ({
  subject = 'patient',
  initialState = 'granted',
  state: controlledState,
  consentStale = false,
  isRecordingConsent = false,
  fills = HISTORY_FILL_EXAMPLES,
  attributedFillCount = 0,
  onRecordConsent,
  defaultExpanded = false,
  onSectionToggled,
  contentMaxHeight,
}) => {
  const [demoState, setDemoState] = useState<ConsentState>(initialState);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const pullTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    onSectionToggled?.(next);
  };

  useEffect(() => setDemoState(initialState), [initialState]);
  useEffect(
    () => () => {
      if (pullTimer.current) clearTimeout(pullTimer.current);
    },
    [],
  );

  // Demo only: recording consent shows the brief "pulling" state, then the
  // fills — the real needed → pulling → granted flow, walkable from stories.
  const advanceDemo = () => {
    setDemoState('pulling');
    pullTimer.current = setTimeout(() => setDemoState('granted'), PULL_SIMULATION_MS);
  };

  const state = controlledState ?? demoState;
  const handleConsent = onRecordConsent ?? advanceDemo;

  // Hidden entirely when the patient isn't e-prescribing-connected AND the
  // host is driving the state (matches the shipped V2 section's behavior);
  // demo mode still renders the explanatory copy for design review.
  if (controlledState === 'unavailable') return null;

  return (
    // Horizontal padding lives on the header and INSIDE the scroll container
    // (not on this wrapper) so the expanded body's scrollbar hugs the host's
    // right edge instead of floating over the fill data.
    <Box
      sx={{
        py: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'backgroundColors.secondary',
      }}
    >
      <Box sx={{ px: 2 }}>
        <SectionHeader
          expanded={expanded}
          onToggle={handleToggle}
          badge={
            expanded
              ? null
              : collapsedBadge(state, fills.length, attributedFillCount, consentStale)
          }
        />
      </Box>
      <Collapse in={expanded}>
        <Box
          sx={{
            maxHeight: contentMaxHeight,
            overflowY: contentMaxHeight ? 'auto' : undefined,
          }}
        >
          <Box sx={{ px: 2, pt: 0.5 }}>
          {state === 'unavailable' && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              {unavailableCopy(subject)}
            </Typography>
          )}
          {state === 'needed' && (
            <NeedsConsent
              onConsent={handleConsent}
              subject={subject}
              disabled={isRecordingConsent}
            />
          )}
          {state === 'pulling' && <Pulling />}
          {state === 'granted' &&
            (consentStale ? (
              // Stale + existing fills coexist: older pulls landed before the
              // refresh started failing. Show what we have under the warning —
              // including the attributed-fills note when SureScripts data
              // lives in the medication drawers above — but never the plain
              // "no fills" copy: absence proves nothing while the refresh is
              // blocked.
              <>
                <StaleRefresh
                  onReconfirm={handleConsent}
                  subject={subject}
                  disabled={isRecordingConsent}
                />
                {(fills.length > 0 || attributedFillCount > 0) && (
                  <Granted
                    fills={fills}
                    subject={subject}
                    attributedFillCount={attributedFillCount}
                  />
                )}
              </>
            ) : (
              <Granted
                fills={fills}
                subject={subject}
                attributedFillCount={attributedFillCount}
              />
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
